import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { RepayObjective } from '@/features/actions/flavours/repay/types'
import { Objective } from '@/features/actions/logic/types'
import { DialogFormNormalizedData } from '@/features/dialogs/common/logic/form'
import { MarketInfo, Reserve } from '@/features/sparklend/common/data/market-info/marketInfo'
import { MarketWalletInfo } from '@/features/sparklend/common/data/useMarketWalletInfo'
import {
  GetRepayMaxValueParams,
  getRepayMaxValue,
} from '@/features/sparklend/common/utils/action-max-value-getters/getRepayMaxValue'

export interface UseCreateRepayObjectivesParams {
  formData: DialogFormNormalizedData
  reserve: Reserve
  marketInfoIn1Epoch: MarketInfo
  marketInfoIn2Epochs: MarketInfo
  walletInfo: MarketWalletInfo
}

export function useCreateRepayObjectives({
  formData,
  reserve,
  marketInfoIn1Epoch,
  marketInfoIn2Epochs,
  walletInfo,
}: UseCreateRepayObjectivesParams): Objective[] {
  const reserveSymbol = reserve.token.symbol
  const payTokenSymbol = formData.token.symbol

  const isPayTokenNativeAsset = marketInfoIn1Epoch.nativeAssetInfo.nativeAssetSymbol === payTokenSymbol
  const nativeAssetReserve = marketInfoIn1Epoch.findOnePositionBySymbol(
    marketInfoIn1Epoch.nativeAssetInfo.nativeAssetSymbol,
  ).reserve

  const balance = walletInfo.findWalletBalanceForSymbol(payTokenSymbol)
  const chainConfigParams: Pick<GetRepayMaxValueParams, 'chain'> = {
    chain: {
      minRemainingNativeAsset: marketInfoIn1Epoch.nativeAssetInfo.minRemainingNativeAssetBalance,
    },
  }

  const repayMaxValueIn1Epoch = getRepayMaxValue({
    asset: {
      status: marketInfoIn1Epoch.findOnePositionBySymbol(reserveSymbol).reserve.status,
      isNativeAsset: isPayTokenNativeAsset,
    },
    user: {
      balance,
      debt: marketInfoIn1Epoch.findOnePositionBySymbol(reserveSymbol).borrowBalance,
    },
    ...chainConfigParams,
  })
  const repayMaxValueIn2Epochs = getRepayMaxValue({
    asset: {
      status: marketInfoIn2Epochs.findOnePositionBySymbol(reserveSymbol).reserve.status,
      isNativeAsset: isPayTokenNativeAsset,
    },
    user: {
      balance,
      debt: marketInfoIn2Epochs.findOnePositionBySymbol(reserveSymbol).borrowBalance,
    },
    ...chainConfigParams,
  })

  const tryFullRepay = formData.isMaxSelected && balance.gt(repayMaxValueIn1Epoch)

  const repayObjective: RepayObjective = {
    type: 'repay',
    value: tryFullRepay ? repayMaxValueIn2Epochs : formData.value,
    requiredApproval: tryFullRepay ? repayMaxValueIn1Epoch : formData.value,
    reserve: isPayTokenNativeAsset ? nativeAssetReserve : reserve,
    useAToken: formData.token.isAToken,
  }

  const isUsdsReserve = reserve.token.symbol === TokenSymbol('USDS')
  const isUsdcInput = formData.token.symbol === TokenSymbol('USDC')
  const isAToken = formData.token.isAToken

  if (isUsdsReserve && isUsdcInput && !isAToken) {
    return [
      {
        type: 'swap',
        from: formData.token,
        to: reserve.token,
        amount: repayObjective.value,
      },
      repayObjective,
    ]
  }

  return [repayObjective]
}
